<script setup>
import {useBaseStore} from "~/stores/base";
import {cartEdit, cartRemoveSku} from "~/api/api.cart";
import {Loading} from "@element-plus/icons-vue";
import {bntoQueryPdpButton} from "~/api/api.bnto";
import {globalUpdateUserInfo, updateCart, sleep} from "~/utils/common";
import {
  PATH_REGISTER, PATH_RESUBSCRIBE,
  PATH_RESUME_SUBSCRIPTION,
  PATH_SUBSCRIBE,
  PATH_UNLOCK
} from "~/utils/constants";
import {memberUnPause} from "~/api/api.member";
import {useCartStore} from "~/stores/cart";
import ModalPremiumConfirm from "~/components/product/ModalPremiumConfirm.vue";

const BNTOMessage = useNuxtApp().$BNTOMessage

/** props **/
const props = defineProps({
  product: {
    default: {},
    required: true
  },
  sku: {
    default: "",
    required: true
  }
})

const isButtonChanging = ref(false)
const isLoading = ref(false)
const btnType = ref(0)
const isShowPremiumConfirm = ref(false)
const premiumConfirmed = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const appInit = computed(() => useBaseStore().getterAppInit)
const cartItemList = computed(() => useCartStore().getterCartItemList)

/** watch **/
// immediate 监听 sku，保证在切换大小屏时也能 refreshBtnStatus
// await nextTick() 保证在 refreshBtnStatus 存在后执行
watch(() => props.sku, async newV => {
  await nextTick()
  await refreshBtnStatus(newV)
}, {immediate: true})

watch(cartItemList, async () => {
  // 保证购物车物品变更时，随时刷新当前商品加购状态
  await refreshBtnStatus(props.sku)
})

const refreshBtnStatus = async (sku) => {
  isButtonChanging.value = true
  const res = await bntoQueryPdpButton({sku})
  console.log("bntoQueryPdpButton", res.result)
  btnType.value = res.result.type
  isButtonChanging.value = false
}
const premiumConfirmModal = () => {
  isShowPremiumConfirm.value = true
  return new Promise(resolve => {
    const stop = watch(isShowPremiumConfirm, (val) => {
      // 无论 true -> false（点确认），还是 false -> false（点取消），都返回 premiumConfirmed.value
      if (val === false) {
        stop()
        resolve(premiumConfirmed.value)
      }
    })
  })
}
const confirmPremium = () => {
  premiumConfirmed.value = true
  isShowPremiumConfirm.value = false
}
const cancelPremium = () => {
  premiumConfirmed.value = false
  isShowPremiumConfirm.value = false
}
const handleAdd = async () => {
  if (isLoading.value) return
  if (props.product.premium) {
    const isAgree = await premiumConfirmModal()
    if (!isAgree) return
  }
  try {
    isLoading.value = true
    const res = await cartEdit({sku: props.sku})
    console.log("cartEdit", res.result)
    // ElMessage.success({message: "Add Successfully"})
    await updateCart()
    isLoading.value = false
    // 按钮转为删除
    btnType.value = 2
  } catch (e) {
    await refreshBtnStatus(props.sku)
    isLoading.value = false
  }
}
const handleRemove = async () => {
  if (isLoading.value) return
  try {
    isLoading.value = true
    const res = await cartRemoveSku({sku: props.sku})
    console.log("cartRemoveSku", res.result)
    // ElMessage.success({message: "Remove Successfully"})
    await updateCart()
    isLoading.value = false
    // 按钮转为添加
    btnType.value = 1
  } catch (e) {
    await refreshBtnStatus(props.sku)
    isLoading.value = false
  }
}
const handleUnPause = async () => {
  if (isLoading.value) return
  try {
    isLoading.value = true
    await memberUnPause()
    await sleep(2000)
    await globalUpdateUserInfo()
    isLoading.value = false
    BNTOMessage.sparkle({
      title: "bnto unpaused",
      message: "You can now fill your BNTO by adding your favorite styles!",
    })
  } catch (e) {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="module-product-action-button-mobile" v-if="isMobile">
    <!-- button changing -->
    <div class="btn-primary btn-disabled" v-if="isButtonChanging">
      <el-icon class="is-loading" color="white" size="16">
        <Loading/>
      </el-icon>
    </div>

    <template v-else>
      <!-- 默认 -->
      <div class="btn-primary btn-disabled" v-if="btnType === 0">
        <img class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>

      <!-- 1 add to my bnto -->
      <div
        class="btn-primary"
        :class="{'btn-disabled': isLoading}"
        v-if="btnType === 1"
        @click="handleAdd"
      >
        <img v-if="isLoading" class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        <img v-else class="icon" src="@/assets/img/icon-plus-black.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>

      <!-- 2 remove from bnto -->
      <div
        class="btn-secondary"
        :class="{'btn-disabled-secondary': isLoading}"
        v-if="btnType === 2"
        @click="handleRemove"
      >
        <el-icon v-if="isLoading" class="is-loading" color="#D6D6D6" size="16" style="margin-right: 8px">
          <Loading/>
        </el-icon>
        {{ 'remove from bnto' }}
      </div>

      <!-- 3 -->
      <nuxt-link :to="PATH_REGISTER" class="btn-primary" v-if="btnType === 3">
        <img class="icon" src="@/assets/img/icon-flash.svg" alt="icon">
        <span>{{ 'join now' }}</span>
      </nuxt-link>

      <!-- 4 -->
      <nuxt-link :to="PATH_SUBSCRIBE" class="btn-primary" v-if="btnType === 4">
        {{ 'subscribe' }}
      </nuxt-link>

      <!-- 5 unlock early -->
      <nuxt-link :to="PATH_UNLOCK" class="btn-primary" v-if="btnType === 5">
        {{ 'unlock early' }}
      </nuxt-link>

      <!-- 6 unpause -->
      <div
        class="btn-primary"
        :class="{'btn-disabled': isLoading}"
        v-if="btnType === 6"
        @click="handleUnPause"
      >
        <el-icon v-if="isLoading" class="is-loading" color="white" size="16" style="margin-right: 8px">
          <Loading/>
        </el-icon>
        {{ 'unpause' }}
      </div>

      <!-- 7 resume subscription -->
      <nuxt-link :to="PATH_RESUME_SUBSCRIPTION" class="btn-primary" v-if="btnType === 7">
        {{ 'resume subscription' }}
      </nuxt-link>

      <!-- 8 bnto is full -->
      <div class="btn-primary btn-disabled" v-if="btnType === 8">
        {{ 'bnto is full' }}
      </div>

      <!-- 9 return bnto -->
      <nuxt-link :to="appInit.agreementLink.faqSupportLink" class="btn-secondary" v-if="btnType === 9">
        <span>{{ 'return bnto' }}</span>
        <img class="icon" src="@/assets/img/icon-info.svg" alt="icon">
      </nuxt-link>

      <!-- 10 resubscribe -->
      <nuxt-link :to="PATH_RESUBSCRIBE" class="btn-primary" v-if="btnType === 10">
        {{ 'resubscribe' }}
      </nuxt-link>

      <!-- 11 button disabled -->
      <div class="btn-primary btn-disabled" v-if="btnType === 11">
        <img class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>
    </template>
  </div>
  <div class="module-product-action-button-desktop" v-else>
    <!-- button changing -->
    <div class="btn-primary btn-disabled" v-if="isButtonChanging">
      <el-icon class="is-loading" color="white" size="16">
        <Loading/>
      </el-icon>
    </div>

    <template v-else>
      <!-- 默认 -->
      <div class="btn-primary btn-disabled" v-if="btnType === 0">
        <img class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>

      <!-- 1 add to my bnto -->
      <div
        class="btn-primary"
        :class="{'btn-disabled': isLoading}"
        v-if="btnType === 1"
        @click="handleAdd"
      >
        <img v-if="isLoading" class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        <img v-else class="icon" src="@/assets/img/icon-plus-black.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>

      <!-- 2 remove from bnto -->
      <div
        class="btn-secondary"
        :class="{'btn-disabled-secondary': isLoading}"
        v-if="btnType === 2"
        @click="handleRemove"
      >
        <el-icon v-if="isLoading" class="is-loading" color="#D6D6D6" size="16" style="margin-right: 8px">
          <Loading/>
        </el-icon>
        {{ 'remove from bnto' }}
      </div>

      <!-- 3 -->
      <nuxt-link :to="PATH_REGISTER" class="btn-primary" v-if="btnType === 3">
        <img class="icon" src="@/assets/img/icon-flash.svg" alt="icon">
        <span>{{ 'join now' }}</span>
      </nuxt-link>

      <!-- 4 -->
      <nuxt-link :to="PATH_SUBSCRIBE" class="btn-primary" v-if="btnType === 4">
        {{ 'subscribe' }}
      </nuxt-link>

      <!-- 5 unlock early -->
      <nuxt-link :to="PATH_UNLOCK" class="btn-primary" v-if="btnType === 5">
        {{ 'unlock early' }}
      </nuxt-link>

      <!-- 6 unpause -->
      <div
        class="btn-primary"
        :class="{'btn-disabled': isLoading}"
        v-if="btnType === 6"
        @click="handleUnPause"
      >
        <el-icon v-if="isLoading" class="is-loading" color="white" size="16" style="margin-right: 8px">
          <Loading/>
        </el-icon>
        {{ 'unpause' }}
      </div>

      <!-- 7 resume subscription -->
      <nuxt-link :to="PATH_RESUME_SUBSCRIPTION" class="btn-primary" v-if="btnType === 7">
        {{ 'resume subscription' }}
      </nuxt-link>

      <!-- 8 bnto is full -->
      <div class="btn-primary btn-disabled" v-if="btnType === 8">
        {{ 'bnto is full' }}
      </div>

      <!-- 9 return bnto -->
      <nuxt-link :to="appInit.agreementLink.faqSupportLink" class="btn-secondary" v-if="btnType === 9">
        <span>{{ 'return bnto' }}</span>
        <img class="icon" src="@/assets/img/icon-info.svg" alt="icon">
      </nuxt-link>

      <!-- 10 resubscribe -->
      <nuxt-link :to="PATH_RESUBSCRIBE" class="btn-primary" v-if="btnType === 10">
        {{ 'resubscribe' }}
      </nuxt-link>

      <!-- 11 button disabled -->
      <div class="btn-primary btn-disabled" v-if="btnType === 11">
        <img class="icon" src="@/assets/img/icon-plus-white.svg" alt="icon">
        {{ 'add to my bnto' }}
      </div>
    </template>
  </div>
  <modal-premium-confirm
    :premium-fee="product.premiumFee"
    :open="isShowPremiumConfirm"
    @cancel="cancelPremium"
    @confirm="confirmPremium"
  />
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-action-button-mobile {
  width: 100%;
}

.module-product-action-button-desktop {
  margin-top: 24px;
  width: 345px;
}
</style>
